import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import Link from "@mui/material/Link";
import { List, useDataGrid } from "@refinedev/mui";
import { useCreate, useInvalidate, useOne, useUpdate, type HttpError } from "@refinedev/core";
import { Link as RouterLink } from "react-router";
import { axiosInstance } from "@/providers/data";
import { FRAMING_LABEL, formatScore, type HeldScores } from "@/lib/evidence";
import { v2EntryUrl, type Storefront } from "@/lib/storefront";

const RESOURCE = "admin/content-reports";

// `upload` is filed by the SYSTEM when an upload is held as evidence (the
// server's ncmec.md §4.6): no reporter, reason `minor_sexual`, and `detail`
// carrying the hold id and nothing else.
//
// The `v2_*` types are Story Studio's (the server's
// v2-reports-blocking-deletion.md D3): an experience or a published character
// (`v2_entry`), a comment or reply on its wall (`v2_comment`), a line in a
// player's own play-through or chat (`v2_message`), and a cast member, of an
// entry or of the reporter's own story (`v2_character`). A v2 creator is filed
// as `creator`, like v1's.
//
// `v2_generated` is filed by the SYSTEM too: a picture the Studio generated that
// the server's output check held as an apparent minor (its imx-explicit.md
// §4.6). Like `upload`, its `detail` is the hold id and nothing else, and it is
// decided on its evidence hold. A system `creator` report whose detail starts
// `floor:` is the server's input screen putting an account in front of a
// reviewer after three refused requests in a day (§4.5).
type ReportTargetType =
  | "creator"
  | "story"
  | "comment"
  | "message"
  | "character"
  | "upload"
  | "v2_entry"
  | "v2_comment"
  | "v2_message"
  | "v2_character"
  | "v2_generated";
type ReportStatus = "open" | "reviewing" | "actioned" | "dismissed";
type ReportOrder = "due" | "newest";
// `flow=v1|v2` on the list; "all" sends nothing.
type ReportFlow = "all" | "v1" | "v2";
// `POST admin/content-reports/:id/actions`. Each one writes a history event and
// moves an open (or reviewing) report to `actioned`.
type ReportAction = "remove_comment" | "take_down" | "restore";

// What the server captured when a v2 report was filed (§4.4 of the server's
// plan): the reported words as the SERVER read them, their author, any media,
// the entry, and which storefront the report came from. It outlives the thing
// it describes — a removed comment or a deleted account leaves it as it was.
type V2ReportSnapshot = {
  storefront: Storefront;
  entry?: { id: string; libraryId: number | null; title: string; kind: "story" | "character"; ownerHandle?: string };
  // `at` is epoch ms.
  words?: { text: string; speaker?: string; by?: { name: string; handle?: string }; at?: number };
  media?: { kind: string; url: string }[];
  character?: string;
  // `client`: the server could not read the words, and kept the reporter's
  // excerpt instead. `import`: backfilled from the engine's old report files.
  source: "server" | "client" | "import";
  // Import only: the words were already gone when the report was imported.
  missing?: boolean;
  // Import / old-route only: the engine's own reason id (minors, hate, …).
  upstreamReason?: string;
};

// One entry of `GET admin/content-reports/:id/events`, oldest first. Append-only:
// every status change and every action writes one.
type ContentReportEvent = {
  id: number;
  reportId: number;
  at: string;
  actorId: number | null;
  actorName: string | null;
  kind: "status" | ReportAction;
  detail: { from?: string; to?: string; note?: string } | null;
};

// One row of GET /api/v2/admin/content-reports. `reporterId` and `reporterName`
// are null when the account that filed it is gone; the report itself survives —
// it carries no foreign key to its target, and since migration 0066 none to
// its reporter either, so deleting either side does not erase the record that
// something was reported.
type ContentReportRecord = {
  id: number;
  reporterId: number | null;
  reporterName: string | null;
  reporterEmail: string | null;
  targetType: ReportTargetType;
  // The row target for creator/story/comment: a comment or account id (a
  // historical v1 `story` row keeps its integer story id here). Null for
  // message and character, which are addressed by the story/character/session/
  // turn columns instead — a character is a name inside a story, and a sandbox
  // message was never stored server-side.
  targetId: number | null;
  // The entry the row is about, by its hex shelf id (e.g. "0925d52c"): the
  // reported entry, the one a comment is on, the one a character belongs to,
  // or the one a reported line's story came from; kept after its library row
  // changes or goes. Null for a row about no entry, and for every v1 row (a
  // v1 story has no hex id).
  storyId: string | null;
  characterName: string | null;
  sessionId: string | null;
  turnId: string | null;
  // The reported message as the player saw it, captured at report time. The
  // only copy anyone has — the server never persists sandbox turns.
  excerpt: string | null;
  // What was actually reported — the message text, "<character> in <story>",
  // the comment's text, the story's title, or the creator's name. Null when
  // the target is gone: reports deliberately outlive what they report, so
  // that is a normal state and renders as deleted.
  targetSummary: string | null;
  // The story a story/message/character report is about. Null for creator and
  // comment rows, and for a story since deleted.
  storyTitle: string | null;
  reason: string;
  detail: string | null;
  status: ReportStatus;
  reviewedBy: number | null;
  reviewedAt: string | null;
  // The 24-hour clock. `firstResponseDueAt` is stamped at filing;
  // `firstRespondedAt` is set once, on the first move out of `open`.
  firstResponseDueAt: string | null;
  firstRespondedAt: string | null;
  // Whole hours to the deadline, computed server-side so the queue can sort
  // on it. Negative once breached.
  hoursRemaining: number | null;
  createdAt: string;
  // How many people have reported this same target. The signal that separates
  // one person's grievance from a pattern, and the reason this is a column
  // rather than something a moderator counts by eye.
  targetReportCount: number;
  // v2 only. A wall comment's `c-…` id and, for a reply, the reply's `r-…`.
  // For `v2_message`, `sessionId` is the player's story uuid and `turnId` the
  // line's message id.
  v2CommentId: string | null;
  v2ReplyId: string | null;
  // The person the report is about (the entry's owner, the comment's author),
  // resolved by the server at filing. Null for AI output in the reporter's own
  // story, for a seeded author, and once that account is deleted.
  subjectUserId: number | null;
  subjectName: string | null;
  snapshot: V2ReportSnapshot | null;
  // When the entry this row names was taken down; null when it is up, or the
  // row names no entry.
  entryTakenDownAt: string | null;
  // An evidence hold's system report (an upload's, a held picture's): what its
  // hold's classifier scored, read from the hold. Null on every other row;
  // absent from a server from before it.
  heldScores?: HeldScores | null;
};

const STATUSES: readonly ReportStatus[] = ["open", "reviewing", "actioned", "dismissed"];

const STATUS_COLOR: Record<ReportStatus, "warning" | "info" | "error" | "default"> = {
  open: "warning",
  reviewing: "info",
  actioned: "error",
  dismissed: "default",
};

// The write set is the launch pack's six reasons, in its order. The three
// pre-launch values stay renderable: a 2026-08 report filed as `explicit` is
// evidence of what was reported under the taxonomy in force at the time, and
// the rows are deliberately not rewritten. These strings must match the
// client's report sheet exactly — one source.
const REASON_LABEL: Record<string, string> = {
  minor_sexual: "Sexual content involving a minor",
  non_consensual: "Non-consensual or abusive content",
  real_person: "Real person depicted",
  self_harm: "Self-harm or suicide",
  harassment: "Harassment",
  other: "Something else",
  // Pre-launch taxonomy — read-only.
  spam: "Spam (legacy)",
  explicit: "Explicit (legacy)",
  impersonation: "Impersonation (legacy)",
};

const LEGACY_REASONS: readonly string[] = ["spam", "explicit", "impersonation"];

const TARGET_LABEL: Record<ReportTargetType, string> = {
  creator: "Creator",
  story: "Story",
  comment: "Comment",
  message: "Message",
  character: "Character",
  upload: "Upload",
  // A shelf entry is a story or a published character: the snapshot says which.
  v2_entry: "Entry (v2)",
  v2_comment: "Comment (v2)",
  v2_message: "Line (v2)",
  v2_character: "Character (v2)",
  v2_generated: "Generated picture (v2)",
};

// A system report for an evidence hold: its status follows the reviewer's
// decision there, and the server refuses a change from this queue.
const decidedOnHold = (targetType: ReportTargetType): boolean =>
  targetType === "upload" || targetType === "v2_generated";

// Who filed a report that has no reporter: one of the server's own checks, or
// a person whose account has since been deleted.
const systemFiler = (row: { targetType: ReportTargetType; detail: string | null }): string =>
  row.targetType === "upload"
    ? "system (upload classifier)"
    : row.targetType === "v2_generated"
      ? "system (picture check)"
      : row.targetType === "creator" && row.detail?.startsWith("floor:")
        ? "system (input screen)"
        : "(account deleted)";

const FLOW_LABEL: Record<ReportFlow, string> = {
  all: "All",
  v1: "v1",
  v2: "v2",
};

const SOURCE_MARKER: Record<V2ReportSnapshot["source"], string | null> = {
  server: null,
  client: "read from client",
  import: "imported",
};

// The confirm dialog's words for each action. `target` names what is acted on
// ("this story", "the story this comment is on"); `noun` is the entry's kind.
const ACTION_COPY: Record<
  ReportAction,
  {
    label: string;
    title: (target: string) => string;
    body: (noun: string) => string;
    done: string;
    color: "error" | "primary";
  }
> = {
  remove_comment: {
    label: "Remove comment",
    title: () => "Remove this comment?",
    body: () =>
      "Deletes the comment (or reply) from the wall, as the wall's owner can. Its words stay in this report's snapshot.",
    done: "Comment removed",
    color: "error",
  },
  take_down: {
    label: "Take down",
    title: (target) => `Take down ${target}?`,
    body: (noun) =>
      `Unpublishes the ${noun} and keeps it down: its owner can't put it back, delete it or publish it again until it is restored here.`,
    done: "Taken down",
    color: "error",
  },
  restore: {
    label: "Restore",
    title: (target) => `Restore ${target}?`,
    body: (noun) => `Lifts the takedown. The ${noun} stays in its owner's bin until they put it back.`,
    done: "Takedown lifted",
    color: "primary",
  },
};

const EVENT_LABEL: Record<ContentReportEvent["kind"], string> = {
  status: "Status changed",
  remove_comment: "Comment removed",
  take_down: "Taken down",
  restore: "Takedown lifted",
};

// The server's limit on an action's note.
const NOTE_MAX = 1000;

// Why a v2 row has no subject. The row can't tell a deleted account from a
// seeded author: `subject_user_id` is null for both.
const NO_SUBJECT_HINT = {
  person: "No account behind it now: deleted since the report was filed, or a seeded author, which never had one.",
  none: "AI output in the reporter's own story, or a story whose creator has since deleted their account.",
};

const HOUR_MS = 60 * 60 * 1000;
// The pack's first-response window. Amber inside the sweep's warning window
// (six hours), red at or past the deadline.
const WARNING_WINDOW_MS = 6 * HOUR_MS;

// Classified against the precise deadline, not the row's whole-hour
// `hoursRemaining`: that value is floored, so `0` still means up to 59 minutes
// left and must not read as overdue. The list refetches every minute, so a
// row re-renders as its state changes.
const deadlineState = (
  dueAt: string,
  now: number = Date.now()
): { color: "error" | "warning" | "default"; label: string } => {
  const msLeft = new Date(dueAt).getTime() - now;
  if (Number.isNaN(msLeft)) {
    return { color: "default", label: "-" };
  }
  if (msLeft <= 0) {
    const overdue = -msLeft;
    const label =
      overdue < HOUR_MS ? `${Math.ceil(overdue / 60_000)}m overdue` : `${Math.floor(overdue / HOUR_MS)}h overdue`;
    return { color: "error", label };
  }
  const label = msLeft < HOUR_MS ? `${Math.ceil(msLeft / 60_000)}m left` : `${Math.floor(msLeft / HOUR_MS)}h left`;
  return { color: msLeft < WARNING_WINDOW_MS ? "warning" : "default", label };
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

// Local calendar day, for `<input type="date">`. `toISOString()` would give the
// UTC day, which is yesterday or tomorrow for an operator near local midnight.
const isoDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// The shared axios instance rejects with Refine's plain `HttpError` object, not
// an `Error` instance, so `instanceof Error` would hide the server's message.
const errorMessage = (err: unknown): string | undefined => {
  if (err instanceof Error) {
    return err.message;
  }
  if (err && typeof err === "object" && "message" in err && typeof err.message === "string") {
    return err.message;
  }
  return undefined;
};

// The story a v1 row is about, as the moderator can name it. v1 stories have
// no hex id, so `storyId` is null on every v1 row; a historical `story` report
// still carries its integer story in `targetId`, shown as a legacy reference.
// A message or character report names no story any more.
const storyIdOf = (row: ContentReportRecord): string | null => {
  if (row.storyId) {
    return row.storyId;
  }
  if (row.targetType === "story" && row.targetId !== null) {
    return `v1 #${row.targetId}`;
  }
  return null;
};

// A v2 report about Studio content (an entry, a comment, a line, a character).
// Not `v2_generated`: that is a held picture with no entry or story of its own,
// shown like an upload's (its hold link, "decided on its hold").
const isV2 = (row: ContentReportRecord): boolean =>
  row.targetType.startsWith("v2_") && row.targetType !== "v2_generated";

// The shelf entry a v2 row names: the reported entry, the one a comment is on,
// the one a character belongs to, or the one a reported line's story came
// from. Null for a character or a line in the reporter's own story.
const entryIdOf = (row: ContentReportRecord): string | null => row.storyId ?? row.snapshot?.entry?.id ?? null;

// What a moderator can do to a row's content from here. Only v2 rows have
// anything: a v1 target is acted on through its own resource. Take down and
// restore apply to the rows whose `storyId` names an entry (a comment's
// entry is the story it was left on); a line names no entry of its own.
const actionsFor = (row: ContentReportRecord): ReportAction[] => {
  const actions: ReportAction[] = [];
  if (row.targetType === "v2_comment") {
    actions.push("remove_comment");
  }
  const namesEntry =
    row.targetType === "v2_entry" ||
    row.targetType === "v2_comment" ||
    (row.targetType === "v2_character" && Boolean(row.storyId));
  if (namesEntry) {
    actions.push(row.entryTakenDownAt ? "restore" : "take_down");
  }
  return actions;
};

// Whether a v2 row is about someone's content at all: an entry has an owner and
// a comment an author. A line or a character in the reporter's own story is
// AI output, with no person behind it.
const namesAPerson = (row: ContentReportRecord): boolean =>
  row.targetType === "v2_entry" || row.targetType === "v2_comment" || entryIdOf(row) !== null;

// The queue is a clock: `hoursRemaining` and the deadline colouring only move
// when the list is refetched. A minute, not the 5s the live lists use — a
// deadline moves by the hour.
const REFRESH_INTERVAL_MS = 60_000;

/**
 * The SLA report — `GET reports/sla?from=&to=&format=csv` — is the 24-hour
 * compliance artefact. Fetched through the shared axios instance rather than
 * a plain link: token transfer is header-based, so an `<a href>` would arrive
 * unauthenticated. Same construction as the removal queue's export.
 */
const SlaExport = () => {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = React.useState(isoDate(monthAgo));
  const [to, setTo] = React.useState(isoDate(today));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await axiosInstance.get<string>(`/api/v2/${RESOURCE}/reports/sla`, {
        params: { from, to, format: "csv" },
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `content-reports-sla-${from}_${to}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError(errorMessage(err) ?? "Could not download the report.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <TextField
        type="date"
        size="small"
        label="From"
        value={from}
        onChange={(event) => setFrom(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        type="date"
        size="small"
        label="To"
        value={to}
        onChange={(event) => setTo(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={<DownloadIcon />}
        disabled={busy || !from || !to || from > to}
        onClick={() => void download()}
      >
        SLA report (CSV)
      </Button>
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * The "Story" cell for a v2 row: the entry the report names (or the one a
 * comment is on, or a line's story came from), its ids, a link to its page on
 * the storefront the report came from, and whether it is taken down. A line or
 * a character in the reporter's own story names no entry: the cell says so and
 * shows that story's id.
 */
const V2EntryCell = ({ row }: { row: ContentReportRecord }) => {
  const entryId = entryIdOf(row);
  if (!entryId) {
    return (
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" noWrap>
          reporter's own story
        </Typography>
        {row.sessionId && (
          <Typography variant="caption" color="text.secondary" noWrap>
            <code>{row.sessionId}</code>
          </Typography>
        )}
      </Stack>
    );
  }
  const title = row.storyTitle ?? row.snapshot?.entry?.title ?? null;
  // A report without a snapshot predates it; the general storefront is the
  // one every entry was published on until immersivex.
  const url = v2EntryUrl(row.snapshot?.storefront ?? "immersive", entryId);

  return (
    <Stack spacing={0.25} sx={{ minWidth: 0, py: 0.5 }}>
      <Typography variant="body2" noWrap>
        {title ?? <span style={{ opacity: 0.6 }}>(entry gone)</span>}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        <code>{entryId}</code>
        {url && (
          <>
            {" · "}
            <Link href={url} target="_blank" rel="noreferrer">
              Open
            </Link>
          </>
        )}
      </Typography>
      {row.entryTakenDownAt && (
        <Tooltip title={`Taken down ${formatDateTime(row.entryTakenDownAt)}`}>
          <Chip size="small" color="error" label="taken down" sx={{ alignSelf: "flex-start" }} />
        </Tooltip>
      )}
    </Stack>
  );
};

/**
 * The "Reported content" cell for a v2 row that carries a snapshot: the words
 * the server read (or the excerpt it fell back to), who wrote or said them,
 * their media, what the entry is, and which storefront the report came from.
 */
const SnapshotContent = ({ row, snapshot }: { row: ContentReportRecord; snapshot: V2ReportSnapshot }) => {
  const words = snapshot.words;
  const author = words?.by
    ? [words.by.name, words.by.handle ? `@${words.by.handle}` : ""].filter(Boolean).join(" ")
    : (words?.speaker ?? null);
  const said = words?.at ? new Date(words.at).toLocaleString() : null;
  const media = snapshot.media ?? [];
  const marker = SOURCE_MARKER[snapshot.source];
  const character = snapshot.character ?? row.characterName;
  const headline =
    (row.targetType === "v2_entry" && snapshot.entry) || (row.targetType === "v2_character" && character);

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0, py: 0.5 }}>
      {row.targetType === "v2_entry" && snapshot.entry && (
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
          {snapshot.entry.kind === "character" ? "Character" : "Story"} “{snapshot.entry.title}”
          {snapshot.entry.ownerHandle && ` by @${snapshot.entry.ownerHandle}`}
        </Typography>
      )}
      {row.targetType === "v2_character" && character && (
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
          {character}
          {snapshot.entry ? ` in “${snapshot.entry.title}”` : " in the reporter's own story"}
        </Typography>
      )}
      {words?.text ? (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {words.text}
        </Typography>
      ) : snapshot.missing ? (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">
          the words were already gone when this was imported
        </Typography>
      ) : headline ? null : (
        // A snapshot with nothing to show of its own: the row's own summary.
        <Typography variant="body2" color={row.targetSummary ? "text.primary" : "text.disabled"}>
          {row.targetSummary ?? "deleted"}
        </Typography>
      )}
      {(author || said) && (
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
          {author && `— ${author}`}
          {author && said && " · "}
          {said}
        </Typography>
      )}
      {row.targetType === "v2_message" && (
        <Typography variant="caption" color="text.secondary" noWrap>
          story <code>{row.sessionId}</code> · line <code>{row.turnId}</code>
        </Typography>
      )}
      {media.length > 0 &&
        (row.reason === "minor_sexual" ? (
          // Never a link to material a report says involves a minor: it is
          // preserved under the report's evidence hold and reviewed there.
          <Typography variant="caption" color="text.secondary">
            {media.length} media item{media.length === 1 ? "" : "s"}, not linked: review on its{" "}
            <Link component={RouterLink} to="/evidence">
              evidence hold
            </Link>
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {media.map((item, index) => (
              <Link key={`${item.url}-${index}`} href={item.url} target="_blank" rel="noreferrer" variant="caption">
                {item.kind} {index + 1}
              </Link>
            ))}
          </Stack>
        ))}
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
        <Chip
          size="small"
          variant="outlined"
          color={snapshot.storefront === "immersivex" ? "secondary" : "default"}
          label={snapshot.storefront}
        />
        {marker && (
          <Tooltip
            title={
              snapshot.source === "client"
                ? "The server could not read these words when the report was filed; this is the reporter's own copy."
                : "Imported from the engine's old report files."
            }
          >
            <Chip size="small" variant="outlined" color="warning" label={marker} />
          </Tooltip>
        )}
        {snapshot.upstreamReason && (
          <Typography variant="caption" color="text.secondary">
            engine reason: {snapshot.upstreamReason}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

type PendingAction = { row: ContentReportRecord; action: ReportAction };

/**
 * Confirms one moderator action and sends it with an optional note. The row
 * and the report's history are re-read whatever the answer: a 410 (the comment
 * or the story is already gone) is news about the row too.
 */
const ReportActionDialog = ({
  pending,
  open,
  onClose,
}: {
  pending: PendingAction | null;
  open: boolean;
  onClose: () => void;
}) => {
  const invalidate = useInvalidate();
  const { mutate, mutation } = useCreate<ContentReportRecord>();
  const [note, setNote] = React.useState("");

  // Start clean each time it opens, so a note typed for one row is never sent
  // with another.
  React.useEffect(() => {
    if (open) {
      setNote("");
    }
  }, [open, pending]);

  if (!pending) {
    return null;
  }

  const { row, action } = pending;
  const copy = ACTION_COPY[action];
  const noun = row.snapshot?.entry?.kind === "character" ? "character" : "story";
  // On a comment's row, take down and restore act on the entry the comment
  // was left on, never the comment itself; the title says so.
  const target = row.targetType === "v2_comment" ? `the ${noun} this comment is on` : `this ${noun}`;

  const submit = () => {
    mutate(
      {
        resource: `${RESOURCE}/${row.id}/actions`,
        values: { action, note: note.trim() || undefined },
        // The snackbar's headline is `description`; `message` is the line under it.
        successNotification: {
          type: "success",
          description: copy.done,
          message:
            row.status === "open" || row.status === "reviewing"
              ? `Report #${row.id} is now actioned.`
              : `Report #${row.id}`,
        },
        errorNotification: (error) => {
          const failure = error as HttpError | undefined;
          return {
            type: "error",
            description: failure?.message ?? `Could not ${copy.label.toLowerCase()}.`,
            message:
              failure?.statusCode === 410
                ? "Nothing is left to act on. The report and its snapshot stay."
                : `Report #${row.id}: nothing was changed.`,
          };
        },
      },
      {
        onSuccess: onClose,
        onSettled: () => {
          void invalidate({ resource: RESOURCE, invalidates: ["list"] });
          void invalidate({ resource: RESOURCE, invalidates: ["detail"], id: `${row.id}/events` });
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{copy.title(target)}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">{copy.body(noun)}</Typography>
          <Typography variant="caption" color="text.secondary">
            Report #{row.id} · {TARGET_LABEL[row.targetType] ?? row.targetType}
            {entryIdOf(row) && (
              <>
                {" "}
                · entry <code>{entryIdOf(row)}</code>
              </>
            )}
            {row.snapshot?.entry?.title && ` · “${row.snapshot.entry.title}”`}
            {(row.status === "open" || row.status === "reviewing") && ". The report moves to actioned."}
          </Typography>
          <TextField
            label="Note (optional)"
            multiline
            minRows={2}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            inputProps={{ maxLength: NOTE_MAX }}
            helperText={`Kept in the report's history. ${note.length}/${NOTE_MAX}`}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" color={copy.color} onClick={submit} disabled={mutation.isPending}>
          {copy.label}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const eventDotColor = (kind: ContentReportEvent["kind"]): "error" | "success" | "info" => {
  if (kind === "remove_comment" || kind === "take_down") {
    return "error";
  }
  return kind === "restore" ? "success" : "info";
};

/**
 * A report's history, oldest first: every status change and every action,
 * who made it and the note they left. Read-only — the log is append-only on
 * the server too.
 */
const ReportHistoryDrawer = ({ id, onClose }: { id: number | null; onClose: () => void }) => {
  const history = useOne<ContentReportEvent[]>({
    resource: RESOURCE,
    id: id === null ? undefined : `${id}/events`,
    queryOptions: { enabled: id !== null },
  });
  const events = history.result ?? [];

  return (
    <Drawer anchor="right" open={id !== null} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 460 } } }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Report #{id} history</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {history.query.isError && <Alert severity="error">Could not load this report's history.</Alert>}
        {history.query.isLoading && (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        )}
        {history.query.isSuccess && events.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Nothing yet. Changes made before the history was kept are not in it.
          </Typography>
        )}

        {events.length > 0 && (
          <Timeline
            sx={{
              p: 0,
              mt: 2,
              "& .MuiTimelineOppositeContent-root": { flex: 0.45, pl: 0 },
            }}
          >
            {events.map((event, index) => (
              <TimelineItem key={event.id}>
                <TimelineOppositeContent color="text.secondary" sx={{ fontSize: 12 }}>
                  {formatDateTime(event.at)}
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot color={eventDotColor(event.kind)} />
                  {index < events.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ pb: 2 }}>
                  <Typography variant="body2">
                    {event.kind === "status" && event.detail?.to
                      ? `Status: ${event.detail.from ?? "?"} → ${event.detail.to}`
                      : (EVENT_LABEL[event.kind] ?? event.kind)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {event.actorName ?? (event.actorId === null ? "system" : `user #${event.actorId}`)}
                  </Typography>
                  {event.detail?.note && (
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {event.detail.note}
                    </Typography>
                  )}
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </Box>
    </Drawer>
  );
};

export const ContentReportList = () => {
  const [status, setStatus] = React.useState<string>("open");
  // Soonest-due first is the default: with a clock on every row, the one that
  // matters is the one about to breach. Newest-first is the backlog view.
  const [order, setOrder] = React.useState<ReportOrder>("due");
  const [flow, setFlow] = React.useState<ReportFlow>("all");
  // The dialog keeps the last action while it fades out; `actionOpen` is what
  // opens and closes it.
  const [pendingAction, setPendingAction] = React.useState<PendingAction | null>(null);
  const [actionOpen, setActionOpen] = React.useState(false);
  const [historyFor, setHistoryFor] = React.useState<number | null>(null);

  // Server-ordered. Columns are not sortable for the same reason as Story
  // Comments — the shared data provider sends page/limit and `field eq value`
  // filters only, so client-side sorting would reorder just the current page.
  // The ordering travels as a filter because that is the one shape the
  // provider forwards as a query param.
  //
  // The status entry is always present, with `undefined` for "All": Refine
  // keeps a filter in the table state until an entry for the same field
  // arrives with an empty value, so merely omitting it from `permanent` left
  // the previous status applied and "All" still showed only that status.
  const { dataGridProps } = useDataGrid<ContentReportRecord>({
    filters: {
      permanent: [
        { field: "status", operator: "eq" as const, value: status || undefined },
        { field: "sort", operator: "eq" as const, value: order },
        { field: "flow", operator: "eq" as const, value: flow === "all" ? undefined : flow },
      ],
    },
    queryOptions: {
      refetchInterval: REFRESH_INTERVAL_MS,
    },
  });

  const { mutate } = useUpdate();

  const columns = React.useMemo<GridColDef<ContentReportRecord>[]>(
    () => [
      {
        field: "targetType",
        headerName: "Target",
        flex: 0.8,
        minWidth: 170,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {TARGET_LABEL[row.targetType] ?? row.targetType}
              {row.targetId !== null && ` #${row.targetId}`}
              {row.characterName && ` · ${row.characterName}`}
              {row.v2ReplyId && " · reply"}
            </Typography>
            {row.targetReportCount > 1 && (
              <Typography variant="caption" color="error">
                {row.targetReportCount} reports
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: "hoursRemaining",
        headerName: "Deadline",
        flex: 0.8,
        minWidth: 170,
        sortable: false,
        display: "flex",
        // The 24-hour first-response clock. Once a moderator has moved the
        // report out of `open` the clock has stopped and the row says when.
        renderCell: ({ row }) =>
          row.firstRespondedAt ? (
            <Typography variant="caption" color="text.secondary">
              responded {formatDateTime(row.firstRespondedAt)}
            </Typography>
          ) : row.firstResponseDueAt ? (
            <Stack spacing={0.25}>
              {(() => {
                const deadline = deadlineState(row.firstResponseDueAt);
                return (
                  <Chip
                    size="small"
                    label={deadline.label}
                    color={deadline.color}
                    variant={deadline.color === "default" ? "outlined" : "filled"}
                  />
                );
              })()}
              <Typography variant="caption" color="text.secondary">
                due {formatDateTime(row.firstResponseDueAt)}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.disabled">
              -
            </Typography>
          ),
      },
      {
        field: "targetSummary",
        headerName: "Reported content",
        flex: 1.6,
        minWidth: 280,
        sortable: false,
        display: "flex",
        // The column that makes the row actionable. "comment #1" alone tells a
        // moderator nothing they can decide on; the words do. A message
        // report's excerpt is rendered in full and wrapping — it is the whole
        // reason the column exists, and the only copy anyone has.
        renderCell: ({ row }) =>
          // An upload row never renders the upload, or anything said about it: its
          // numeric id only, and a link to its evidence hold, where the material
          // sits behind a reviewer's reveal.
          decidedOnHold(row.targetType) ? (
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {row.targetType === "upload"
                  ? `Upload #${row.targetId}`
                  : // The server files an excerpt only for the creator's own picture sent to a story, never for a
                    // generated one.
                    row.excerpt
                    ? "The creator’s own picture, sent to a story"
                    : "A generated picture, never shown"}
              </Typography>
              {row.detail && (
                <Link component={RouterLink} to={`/evidence/${row.detail}`} variant="caption">
                  Open evidence hold {row.detail}
                </Link>
              )}
            </Stack>
          ) : isV2(row) && row.snapshot ? (
            <SnapshotContent row={row} snapshot={row.snapshot} />
          ) : row.targetType === "message" && row.excerpt ? (
            <Stack spacing={0.25} sx={{ minWidth: 0, py: 0.5 }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {row.excerpt}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.characterName} · session <code>{row.sessionId}</code> · turn <code>{row.turnId}</code>
              </Typography>
            </Stack>
          ) : row.targetType === "v2_message" && row.excerpt ? (
            <Stack spacing={0.25} sx={{ minWidth: 0, py: 0.5 }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {row.excerpt}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.characterName && `${row.characterName} · `}story <code>{row.sessionId}</code> · line{" "}
                <code>{row.turnId}</code>
              </Typography>
            </Stack>
          ) : row.targetSummary ? (
            <Tooltip title={row.targetSummary}>
              <Typography variant="body2" noWrap>
                {row.targetSummary}
              </Typography>
            </Tooltip>
          ) : (
            // Not an error. The report has no foreign key precisely so that
            // deleting the offending content cannot erase the record of it.
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              deleted
            </Typography>
          ),
      },
      {
        field: "subjectUserId",
        headerName: "About",
        flex: 0.7,
        minWidth: 150,
        sortable: false,
        display: "flex",
        // The person the report is about, as the server resolved it at filing
        // (v2 rows). Never typed by the reporter.
        renderCell: ({ row }) => {
          if (row.subjectUserId != null) {
            return (
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Link component={RouterLink} to={`/users/show/${row.subjectUserId}`} variant="body2" noWrap>
                  {row.subjectName ?? `#${row.subjectUserId}`}
                </Link>
                {row.subjectName && (
                  <Typography variant="caption" color="text.secondary">
                    #{row.subjectUserId}
                  </Typography>
                )}
              </Stack>
            );
          }
          if (!isV2(row)) {
            return (
              <Typography variant="body2" color="text.disabled">
                -
              </Typography>
            );
          }
          // Null on a v2 row: the account is gone (the column is set null on
          // delete), the author is one of the engine's seeds, or there was never
          // a person — AI output in the reporter's own story.
          return namesAPerson(row) ? (
            <Tooltip title={NO_SUBJECT_HINT.person}>
              <Typography variant="body2" color="text.disabled" fontStyle="italic">
                deleted or seeded author
              </Typography>
            </Tooltip>
          ) : (
            <Tooltip title={NO_SUBJECT_HINT.none}>
              <Typography variant="body2" color="text.disabled" fontStyle="italic">
                no account
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "storyId",
        headerName: "Story",
        flex: 0.9,
        minWidth: 180,
        sortable: false,
        display: "flex",
        // Keyed on the story the row is ABOUT — its hex `storyId` (the id the
        // library lists), or a historical v1 story report's legacy integer.
        renderCell: ({ row }) => {
          if (isV2(row)) {
            return <V2EntryCell row={row} />;
          }
          // A held picture names the player's own story it was made in by its engine id: no shelf entry, no title.
          if (row.targetType === "v2_generated" && row.sessionId) {
            return (
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  The player’s own story
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  <code>{row.sessionId}</code>
                </Typography>
              </Stack>
            );
          }
          const storyId = storyIdOf(row);
          if (storyId === null) {
            return (
              <Typography variant="body2" color="text.disabled">
                -
              </Typography>
            );
          }
          return (
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {row.storyTitle ?? <span style={{ opacity: 0.6 }}>(story deleted)</span>}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <code>{storyId}</code>
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "reason",
        headerName: "Reason",
        flex: 0.9,
        minWidth: 190,
        sortable: false,
        display: "flex",
        // A hold's report says what its check scored under the reason: "minor"
        // is an apparent minor in a sexual OR suggestive framing, so the reason
        // alone cannot tell explicit from suggestive.
        renderCell: ({ row }) => (
          <Stack spacing={0.5} sx={{ minWidth: 0, py: 0.5 }} alignItems="flex-start">
            <Chip
              size="small"
              label={REASON_LABEL[row.reason] ?? row.reason}
              variant={LEGACY_REASONS.includes(row.reason) ? "outlined" : "filled"}
            />
            {row.heldScores?.framing && (
              <Typography variant="caption" color="text.secondary">
                {FRAMING_LABEL[row.heldScores.framing]}
              </Typography>
            )}
            {row.heldScores && (
              <Typography variant="caption" color="text.secondary">
                minor {formatScore(row.heldScores.minor)}
                {row.heldScores.explicit !== null && ` · explicit ${formatScore(row.heldScores.explicit)}`}
                {row.heldScores.ageAmbiguous !== null && ` · age-ambiguous ${formatScore(row.heldScores.ageAmbiguous)}`}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: "detail",
        headerName: "Detail",
        flex: 1,
        minWidth: 180,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) =>
          row.detail ? (
            <Tooltip title={row.detail}>
              <Typography variant="body2" noWrap>
                {row.detail}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.disabled">
              -
            </Typography>
          ),
      },
      {
        field: "reporterName",
        headerName: "Reporter",
        flex: 0.8,
        minWidth: 160,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {row.reporterName ??
                (row.reporterId === null ? (
                  <span style={{ opacity: 0.6 }}>
                    {systemFiler(row)}
                  </span>
                ) : (
                  `#${row.reporterId}`
                ))}
            </Typography>
            {row.reporterEmail && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.reporterEmail}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: "createdAt",
        headerName: "Filed",
        flex: 0.8,
        minWidth: 170,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <Typography variant="body2">{formatDateTime(row.createdAt)}</Typography>,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.9,
        minWidth: 170,
        sortable: false,
        display: "flex",
        // The only mutation here. Closing a ticket deliberately does NOT touch
        // the content it names — the comment or story is acted on through its
        // own resource, so a moderator has to make that call explicitly. The
        // first move out of `open` is what stops the clock.
        renderCell: ({ row }) =>
          // An upload report follows the reviewer's decision on its evidence hold;
          // the server refuses a change from this queue.
          decidedOnHold(row.targetType) ? (
            <Stack spacing={0.25}>
              <Chip size="small" label={row.status} color={STATUS_COLOR[row.status]} />
              <Typography variant="caption" color="text.secondary">
                decided on its hold
              </Typography>
            </Stack>
          ) : (
            <TextField
              select
              size="small"
              value={row.status}
              onChange={(event) =>
                mutate({
                  resource: RESOURCE,
                  id: row.id,
                  values: { status: event.target.value },
                })
              }
              sx={{ minWidth: 140 }}
            >
              {STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  <Chip
                    size="small"
                    label={value}
                    color={STATUS_COLOR[value]}
                    variant={value === row.status ? "filled" : "outlined"}
                  />
                </MenuItem>
              ))}
            </TextField>
          ),
      },
      {
        field: "actions",
        headerName: "",
        minWidth: 190,
        sortable: false,
        display: "flex",
        // Acting on the content is a separate, explicit step from the status:
        // each action asks first, and each lands in the report's history.
        renderCell: ({ row }) => (
          <Stack spacing={0.5} alignItems="flex-start" sx={{ py: 0.5 }}>
            {actionsFor(row).map((action) => (
              <Button
                key={action}
                size="small"
                variant="outlined"
                color={ACTION_COPY[action].color}
                sx={{ whiteSpace: "nowrap" }}
                onClick={() => {
                  setPendingAction({ row, action });
                  setActionOpen(true);
                }}
              >
                {ACTION_COPY[action].label}
              </Button>
            ))}
            <Button size="small" onClick={() => setHistoryFor(row.id)}>
              History
            </Button>
          </Stack>
        ),
      },
    ],
    [mutate]
  );

  return (
    <List canCreate={false}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Reports across creators, stories, comments, characters and sandbox messages, v1 and Story Studio (v2).
          Every report is owed a first response within 24 hours — moving it out of “open” is what stops the clock.
          Changing a status never removes the reported content. Act on a v1 target through its own resource; on a
          v2 one with the row's own actions. Every status change and action is kept in the report's history.
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={order}
          onChange={(_event, next: ReportOrder | null) => {
            if (next) {
              setOrder(next);
            }
          }}
          aria-label="Ordering"
        >
          <ToggleButton value="due">Due soonest</ToggleButton>
          <ToggleButton value="newest">Newest</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={flow}
          onChange={(_event, next: ReportFlow | null) => {
            if (next) {
              setFlow(next);
            }
          }}
          aria-label="Product"
        >
          {(Object.keys(FLOW_LABEL) as ReportFlow[]).map((value) => (
            <ToggleButton key={value} value={value}>
              {FLOW_LABEL[value]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box sx={{ flexGrow: 1 }} />
        <SlaExport />
      </Stack>
      <DataGrid {...dataGridProps} columns={columns} disableColumnFilter getRowHeight={() => "auto"} />
      <ReportActionDialog pending={pendingAction} open={actionOpen} onClose={() => setActionOpen(false)} />
      <ReportHistoryDrawer id={historyFor} onClose={() => setHistoryFor(null)} />
    </List>
  );
};
